import {
  Component,
  input,
  InputSignal,
  OnInit,
  OnDestroy,
  output,
  OutputEmitterRef,
  TemplateRef,
  ViewChild,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize, Subject, takeUntil } from 'rxjs';
import { couponUser } from '../data/user.data';
import { CouponsService } from '../services/coupons.service';
import { CouponsItemResponse } from '../model/coupons-response';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { DomianProviderService } from '../services/domain-provider.service';
import { ClipRequest } from '../model/clip-response';
import { envs } from '../data/env.data';
import { ActivatedRoute } from '@angular/router';
import { getBannerFromUrl } from '../services/appconfig.service';

@Component({
  selector: 'app-coupons-button',
  standalone: true,
  imports: [CommonModule, NgbModule],
  templateUrl: './coupons-button.component.html',
  styleUrl: './coupons-button.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CouponsButtonComponent implements OnInit, OnDestroy {
  coupon: InputSignal<CouponsItemResponse | undefined> = input<
    CouponsItemResponse | undefined
  >();

  private readonly couponService = inject(CouponsService);
  private readonly modalService = inject(NgbModal);
  private readonly bannerService = inject(DomianProviderService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroy$ = new Subject<void>();

  readonly isLoggedIn = signal(false);
  readonly banner = computed(() => {
    return envs.useParamsForBanner
      ? getBannerFromUrl()
      : this.bannerService.getBanner();
  });
  readonly isClipping = signal(false);
  readonly modalOptions = signal<{
    warningText?: string;
    okText?: string;
  }>({});

  // Template reference
  @ViewChild('content') content: TemplateRef<any> | undefined;

  // Output for parent communication
  readonly doLogin: OutputEmitterRef<boolean> = output<boolean>();

  readonly canClip = computed(() => {
    const couponValue = this.coupon();
    return (
      couponValue && !this.isClipping() && couponValue.status === 'available'
    );
  });

  readonly getClassString = computed(() => {
    if (this.coupon()?.status === 'available') {
      return `${this.banner()}-btn-primary`;
    }

    return 'btn-outline-dark';
  });
  readonly buttonText = computed(() => {
    if (!this.isLoggedIn()) return 'Login to Load';
    if (this.isClipping()) return 'Clipping...';
    const couponValue = this.coupon();
    return couponValue?.status === 'available'
      ? 'Load Coupon'
      : 'Coupon Loaded';
  });

  ngOnInit(): void {
    this.isLoggedIn.set(couponUser.isLoggedIn);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clipCoupon(): void {
    const couponValue = this.coupon();
    if (!couponValue || this.isClipping()) return;

    const clipRequest: ClipRequest = {
      couponId: couponValue.id || '',
    };

    this.isClipping.set(true);

    this.couponService
      .clipCoupon([clipRequest], this.banner())
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isClipping.set(false))
      )
      .subscribe({
        next: (clip) => {
          if (clip[0]?.result && couponValue) {
            couponValue.group = 'clipped';
            couponValue.status = 'clipped';
          } else {
            this.setModalOptions(clip[0]?.message || 'Unknown error occurred');
            this.openModal();
          }
        },
        error: (error) => {
          this.setModalOptions('An error occurred while clipping the coupon');
          this.openModal();
          console.error('Error clipping coupon:', error);
        },
      });
  }

  private setModalOptions(warningText: string): void {
    this.modalOptions.set({
      warningText: warningText,
      okText: 'OK',
    });
  }
  openModal(): void {
    if (!this.content) return;

    this.modalService.open(this.content, {
      centered: true,
      windowClass: 'coupon-app',
    });
  }
  redirectTologin() {
    this.doLogin.emit(true);
  }
}
